import React, { useEffect, useState, useCallback } from "react";
import { ScrollText, LogIn, Wallet, ShoppingCart, Boxes, Utensils, Cpu, Settings as SettingsIcon, UserCog } from "lucide-react";
import { EmptyState } from "@/components/bio/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const META = {
    login: { icon: LogIn, label: "Inicio de sesión", color: "#00508A" },
    recharge: { icon: Wallet, label: "Recarga", color: "#1BC5BD" },
    sale: { icon: ShoppingCart, label: "Venta", color: "#2A87C8" },
    inventory: { icon: Boxes, label: "Inventario", color: "#F5A623" },
    menu: { icon: Utensils, label: "Menú", color: "#1464A0" },
    device: { icon: Cpu, label: "Dispositivo", color: "#6B7A99" },
    dispenser: { icon: Cpu, label: "Dispensadora", color: "#6B7A99" },
    student: { icon: UserCog, label: "Estudiante", color: "#00508A" },
    settings: { icon: SettingsIcon, label: "Configuración", color: "#B71C1C" },
};

const FILTERS = ["all", "login", "recharge", "sale", "inventory", "menu", "device", "dispenser", "student", "settings"];

export default function AuditView() {
    const [rows, setRows] = useState(null);
    const [action, setAction] = useState("all");

    const load = useCallback(() => {
        setRows(null);
        api.get("/audit", { params: action !== "all" ? { action } : {} }).then(({ data }) => setRows(data));
    }, [action]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Auditoría</h1>
                    <p className="text-bio-text-secondary mt-1 text-sm">Registro de acciones realizadas en la plataforma.</p>
                </div>
                <Select value={action} onValueChange={setAction}>
                    <SelectTrigger data-testid="audit-filter" className="h-11 rounded-bio bg-white border-[1.5px] min-w-[180px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {FILTERS.map((f) => <SelectItem key={f} value={f}>{f === "all" ? "Todas las acciones" : META[f]?.label || f}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {!rows ? (
                <Skeleton className="h-64 rounded-bio-card" />
            ) : rows.length === 0 ? (
                <div className="bg-white rounded-bio-card border border-bio-border-subtle"><EmptyState icon={ScrollText} title="Sin registros" description="Aún no hay acciones registradas para este filtro." /></div>
            ) : (
                <div className="bg-white rounded-bio-card border border-bio-border-subtle shadow-bio-sm divide-y divide-bio-border-subtle" data-testid="audit-list">
                    {rows.map((r) => {
                        const m = META[r.action] || { icon: ScrollText, label: r.action, color: "#6B7A99" };
                        const Icon = m.icon;
                        return (
                            <div key={r.id} className="flex items-start gap-3 p-4">
                                <div className="h-9 w-9 rounded-bio flex items-center justify-center shrink-0" style={{ backgroundColor: m.color + "1A", color: m.color }}><Icon className="h-4 w-4" /></div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold">{m.label}</span>
                                        <span className="text-xs text-bio-text-muted">· {r.user_email}</span>
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-bio-text-muted bg-bio-bg-alt px-1.5 py-0.5 rounded">{r.user_role}</span>
                                    </div>
                                    <p className="text-sm text-bio-text-secondary mt-0.5">{r.detail}</p>
                                </div>
                                <span className="text-xs text-bio-text-muted shrink-0">{formatDateTime(r.created_at)}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
